import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Loader2, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bulk add/remove a tag across selected contacts. Powers the tags that the
// advanced List filters reference (e.g. "Tech Supplier", "Signed Up").
const BulkTagDialog = ({ ids, open, onOpenChange, onDone }) => {
  const [tag, setTag] = useState('');
  const [action, setAction] = useState('add');
  const [existing, setExisting] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTag(''); setAction('add');
    axios.get(`${API}/lists/fields`, { params: { object_type: 'contact' } })
      .then(r => {
        const tagField = (r.data.groups || []).flatMap(g => g.fields || []).find(f => f.field === 'tags');
        setExisting(tagField?.options || []);
      })
      .catch(() => setExisting([]));
  }, [open]);

  const apply = async () => {
    const t = tag.trim();
    if (!t) { toast.error('Enter a tag'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/contacts/bulk-tag`, { contact_ids: ids, tags: [t], action });
      toast.success(`Tag "${t}" ${action === 'add' ? 'added to' : 'removed from'} ${res.data.updated} contact(s)`);
      onOpenChange(false);
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tags');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="bulk-tag-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white flex items-center gap-2">
            <Tag className="w-5 h-5 text-violet-400" /> Tag {ids.length} contact{ids.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription className="text-slate-400">Add or remove a tag in bulk. Tags can then be used in List filters.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <Label className="text-slate-300 text-sm">Action</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="bg-slate-950 border-slate-800 h-9" data-testid="bulk-tag-action"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="add">Add tag</SelectItem>
                <SelectItem value="remove">Remove tag</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Tag</Label>
            <Input value={tag} onChange={e => setTag(e.target.value)} placeholder="e.g. Tech Supplier"
                   className="bg-slate-950 border-slate-800" data-testid="bulk-tag-input" />
          </div>
          {existing.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="bulk-tag-existing">
              {existing.slice(0, 20).map(t => (
                <button type="button" key={t} onClick={() => setTag(t)}
                        className="px-2 py-1 rounded-md text-xs bg-slate-950 border border-slate-700 text-slate-300 hover:border-violet-500">
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button className="bg-violet-600 hover:bg-violet-500" onClick={apply} disabled={saving} data-testid="bulk-tag-apply">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</> : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkTagDialog;
